import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { verifyColleagueInvitation } from '~/features/employer/colleague-invites/api';
import { paths } from '~/routing/routes';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { AuthLoading } from './AuthLoading';
import { SignUpPage } from './SignUpPage';

// [MCV-1796] The pre-fill is a nicety, so the whole sign-up page must not wait on it for long: past
// this deadline the form renders without the address. `withRequestDeadline` settles exactly once, so
// a result that arrives after the deadline can no longer change what the page rendered with.
const INVITATION_LOOKUP_DEADLINE_MS = 5_000;

/**
 * [MCV-1796] An invitee should create the account with the invited address, so
 * the invitation's email pre-fills Clerk's field. The code is already in the
 * URL and the verify endpoint is public, so the address never has to travel in
 * the link. Clerk reads `initialValues` when it mounts, so the form waits for
 * the lookup; a failed or stalled lookup just means no pre-fill.
 */
const useInvitedEmail = (invitationCode: string | null) => {
  const [state, setState] = useState<{ code: string | null; email: string | null }>({
    code: null,
    email: null,
  });

  useEffect(() => {
    if (!invitationCode) return;
    let active = true;
    withRequestDeadline(
      () => verifyColleagueInvitation(invitationCode),
      INVITATION_LOOKUP_DEADLINE_MS
    )
      .then((response) => {
        if (active) {
          setState({
            code: invitationCode,
            email: response.status === 'PENDING' ? response.invitationEmail : null,
          });
        }
      })
      .catch(() => {
        if (active) setState({ code: invitationCode, email: null });
      });
    return () => {
      active = false;
    };
  }, [invitationCode]);

  return {
    // [MCV-1796] An empty `?invitationCode=` is skipped by the lookup effect, so it has to read as
    // "no invitation" here too; `!= null` would leave the page loading forever.
    loading: !!invitationCode && state.code !== invitationCode,
    email: state.code === invitationCode ? state.email : null,
  };
};

export const EmployerSignUpPage = () => {
  const [searchParams] = useSearchParams();
  const invitationCode = searchParams.get('invitationCode');
  const returnTo = invitationCode
    ? `${paths.employerUserInvitation.accept}?code=${encodeURIComponent(invitationCode)}`
    : null;
  const { loading, email } = useInvitedEmail(invitationCode);

  if (loading) return <AuthLoading message="Loading your invitation…" />;

  return (
    <SignUpPage path={paths.publicAuth.signUpEmployer} returnTo={returnTo} initialEmail={email} />
  );
};
